import React from 'react'
import { X, DollarSign, Clock, Users, Star, Save, Trash2, Settings } from 'lucide-react'
import { toast } from '../lib/toast'

interface Service {
  id: string
  name: string
  category: string
  base_price: number
  duration_hours: number
  complexity_level: number
  requires_team: boolean
  min_team_size: number
  description: string
  is_active: boolean
  created_at: string
  updated_at: string
  popular?: boolean
  price_per_room?: number
}

interface ServiceModalProps {
  service: Service | null
  isOpen: boolean
  onClose: () => void
  onSave: (service: Service) => void
  onDelete?: (serviceId: string) => void
  mode: 'view' | 'edit' | 'create'
}

export default function ServiceModal({ service, isOpen, onClose, onSave, onDelete, mode }: ServiceModalProps) {
  const [formData, setFormData] = React.useState<Partial<Service>>({
    name: '',
    category: 'cleaning',
    base_price: 0,
    duration_hours: 1,
    complexity_level: 1,
    requires_team: false,
    min_team_size: 1,
    description: '',
    is_active: true,
    popular: false,
    price_per_room: 0
  })

  React.useEffect(() => {
    if (service && mode !== 'create') {
      setFormData(service)
    } else if (mode === 'create') {
      setFormData({
        name: '',
        category: 'cleaning',
        base_price: 0,
        duration_hours: 1,
        complexity_level: 1,
        requires_team: false,
        min_team_size: 1,
        description: '',
        is_active: true,
        popular: false,
        price_per_room: 0
      })
    }
  }, [service, mode])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name || !formData.description) {
      toast.error('Name and description are required')
      return
    }

    if (formData.base_price && formData.base_price <= 0) {
      toast.error('Base price must be greater than 0')
      return
    }

    if (formData.duration_hours && formData.duration_hours <= 0) {
      toast.error('Duration must be greater than 0')
      return
    }

    const now = new Date().toISOString()

    if (mode === 'create') {
      const newService: Service = {
        id: Date.now().toString(),
        name: formData.name!,
        category: formData.category!,
        base_price: formData.base_price!,
        duration_hours: formData.duration_hours!,
        complexity_level: formData.complexity_level!,
        requires_team: formData.requires_team!,
        min_team_size: formData.min_team_size!,
        description: formData.description!,
        is_active: formData.is_active!,
        created_at: now,
        updated_at: now,
        popular: formData.popular,
        price_per_room: formData.price_per_room
      }
      onSave(newService)
      toast.success('Service created successfully')
    } else if (service) {
      const updatedService: Service = {
        ...service,
        name: formData.name!,
        category: formData.category!,
        base_price: formData.base_price!,
        duration_hours: formData.duration_hours!,
        complexity_level: formData.complexity_level!,
        requires_team: formData.requires_team!,
        min_team_size: formData.min_team_size!,
        description: formData.description!,
        is_active: formData.is_active!,
        updated_at: now,
        popular: formData.popular,
        price_per_room: formData.price_per_room
      }
      onSave(updatedService)
      toast.success('Service updated successfully')
    }
    
    onClose()
  }

  const handleDelete = () => {
    if (service && onDelete && confirm('Are you sure you want to delete this service?')) {
      onDelete(service.id)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {mode === 'view' ? 'Service Details' : mode === 'edit' ? 'Edit Service' : 'Create Service'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 bg-white dark:bg-gray-800 rounded-b-xl">
          <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Service Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="e.g., Standard House Cleaning"
                disabled={mode === 'view'}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                disabled={mode === 'view'}
              >
                <option value="cleaning">Cleaning</option>
                <option value="commercial">Commercial</option>
                <option value="specialized">Specialized</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Describe the service in detail..."
              rows={3}
              disabled={mode === 'view'}
              required
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                <DollarSign className="inline w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                Base Price
              </label>
              <input
                type="number"
                value={formData.base_price}
                onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="150"
                min="0"
                step="0.01"
                disabled={mode === 'view'}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                <Clock className="inline w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                Duration (hours)
              </label>
              <input
                type="number"
                value={formData.duration_hours}
                onChange={(e) => setFormData({ ...formData, duration_hours: parseFloat(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="3"
                min="0.5"
                step="0.5"
                disabled={mode === 'view'}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                <Star className="inline w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                Complexity Level
              </label>
              <select
                value={formData.complexity_level}
                onChange={(e) => setFormData({ ...formData, complexity_level: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                disabled={mode === 'view'}
              >
                <option value={1}>1 - Very Easy</option>
                <option value={2}>2 - Easy</option>
                <option value={3}>3 - Medium</option>
                <option value={4}>4 - Hard</option>
                <option value={5}>5 - Very Hard</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Price per Room (optional)
              </label>
              <input
                type="number"
                value={formData.price_per_room || ''}
                onChange={(e) => setFormData({ ...formData, price_per_room: parseFloat(e.target.value) || undefined })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="25"
                min="0"
                step="0.01"
                disabled={mode === 'view'}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                <Users className="inline w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
                Team Required
              </label>
              <select
                value={formData.requires_team?.toString()}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  requires_team: e.target.value === 'true',
                  min_team_size: e.target.value === 'true' ? Math.max(formData.min_team_size || 1, 2) : 1
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                disabled={mode === 'view'}
              >
                <option value="false">No (Individual)</option>
                <option value="true">Yes (Team)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Minimum Team Size
              </label>
              <input
                type="number"
                value={formData.min_team_size}
                onChange={(e) => setFormData({ ...formData, min_team_size: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:focus:ring-green-400 dark:focus:border-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="1"
                min={formData.requires_team ? 2 : 1}
                disabled={mode === 'view'}
                required
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded text-green-600 focus:ring-green-500 dark:text-green-400 dark:focus:ring-green-400"
                disabled={mode === 'view'}
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Active Service</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.popular}
                onChange={(e) => setFormData({ ...formData, popular: e.target.checked })}
                className="rounded text-yellow-600 focus:ring-yellow-500 dark:text-yellow-400 dark:focus:ring-yellow-400"
                disabled={mode === 'view'}
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Popular Service</span>
            </label>
          </div>

          {mode === 'view' && service && (
            <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Created</p>
                  <p className="text-gray-900 dark:text-gray-100">{new Date(service.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Last Updated</p>
                  <p className="text-gray-900 dark:text-gray-100">{new Date(service.updated_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between pt-4">
            <div>
              {mode === 'edit' && onDelete && service && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this service?')) {
                      onDelete(service.id)
                      onClose()
                    }
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {mode === 'view' ? 'Close' : 'Cancel'}
              </button>
              
              {mode !== 'view' && (
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>{mode === 'create' ? 'Create' : 'Save'}</span>
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
